import { useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip, X, FilePdf, ImageSquare } from "@phosphor-icons/react";
import api from "../../lib/api";
import { apiError, btnGhost } from "./util";

const ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf";

export async function openAttachment(id) {
    try {
        const res = await api.get(`/reporting/attachments/${id}/file`, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        window.open(url, "_blank", "noopener");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
        toast.error(await apiError(e));
    }
}

export function AttachmentChip({ a, onRemove }) {
    const Icon = a.content_type === "application/pdf" ? FilePdf : ImageSquare;
    return (
        <div className="flex items-center gap-2 bg-white border border-mist rounded-sm px-2.5 py-1.5 text-xs" data-testid={`attachment-${a.id}`}>
            <Icon size={16} className="text-sage-dark shrink-0" />
            <button type="button" onClick={() => openAttachment(a.id)} className="truncate max-w-[180px] hover:underline text-left" data-testid={`attachment-open-${a.id}`}>
                {a.original_filename}
            </button>
            {onRemove && (
                <button type="button" onClick={() => onRemove(a)} className="text-neutral-400 hover:text-red-600" aria-label="Hapus" data-testid={`attachment-remove-${a.id}`}>
                    <X size={14} />
                </button>
            )}
        </div>
    );
}

export default function ProofUploader({ brand, date, kind, title, hint, items, onChange, required }) {
    const ref = useRef(null);
    const [busy, setBusy] = useState(false);
    const onFiles = async (files) => {
        setBusy(true);
        const added = [];
        for (const f of files) {
            const fd = new FormData();
            fd.append("brand", brand);
            fd.append("reporting_date", date);
            fd.append("kind", kind);
            fd.append("file", f);
            try {
                const { data } = await api.post("/reporting/attachments", fd);
                added.push(data);
            } catch (e) {
                toast.error(`${f.name}: ${await apiError(e)}`);
            }
        }
        onChange([...items, ...added]);
        setBusy(false);
        if (ref.current) ref.current.value = "";
    };
    const remove = async (a) => {
        if (a.in_revision) {
            onChange(items.filter((x) => x.id !== a.id));
            return;
        }
        try {
            await api.delete(`/reporting/attachments/${a.id}`);
            onChange(items.filter((x) => x.id !== a.id));
        } catch (e) {
            toast.error(await apiError(e));
        }
    };
    return (
        <div className="space-y-2" data-testid={`proof-${kind}`}>
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="text-sm text-ink">{title}{required && <span className="text-red-600"> *</span>}</div>
                    <div className="text-xs text-neutral-500">{hint}</div>
                </div>
                <button type="button" onClick={() => ref.current?.click()} disabled={busy} className={btnGhost} data-testid={`proof-add-${kind}`}>
                    <Paperclip size={14} /> {busy ? "Mengunggah…" : "Lampirkan"}
                </button>
                <input ref={ref} type="file" accept={ACCEPT} multiple className="hidden" onChange={(e) => onFiles([...e.target.files])} data-testid={`proof-input-${kind}`} />
            </div>
            {items.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {items.map((a) => <AttachmentChip key={a.id} a={a} onRemove={remove} />)}
                </div>
            )}
        </div>
    );
}
